<template>
  <div class="app-container">
    <div class="card card-custom mb-5">
      <div class="card-body">
        <div class="calendar-container">
          <div class="calendar-wrapper">
            <table class="calendar-table">
              <colgroup>
                <col class="name-column" />
                <col
                  v-for="day in daysOfWeek"
                  :key="`col-${day}`"
                  class="day-column"
                />
              </colgroup>
              <thead>
                <tr class="header-row">
                  <th class="name-cell">Tổng khách: {{ userPackages.length }}</th>
                  <th v-for="(day, dayIndex) in daysOfWeek" :key="day" class="day-header" :class="{ 'current-day': dayIndex === currentDayIndex }">
                    {{ day }}<br>{{ currentWeekDates[dayIndex] }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="userPackage in userPackages"
                  :key="userPackage.id"
                  class="calendar-row"
                >
                  <td class="name-cell">
                    <p class="font-weight-bold ">{{ userPackage.account.fullName }}</p>
                    <p class="package-name">Gói {{ userPackage.tranServicePackageName }}</p>
                    <p>{{ userPackage.inWeek }}</p>
                    <p>Hoàn thành {{ userPackage.completedActivityCount }}</p>
                    <b-button 
                      size="sm" 
                      variant="success" 
                      class="mt-2"
                      @click="viewPatientDetail(userPackage.accountId)"
                    >
                      Xem chi tiết
                    </b-button>
                  </td>
                  <td
                    v-for="(day, dayIndex) in daysOfWeek"
                    :key="`${userPackage.id}-${day}`"
                    class="day-cell"
                    :class="{ 'current-day': dayIndex === currentDayIndex }"
                  >
                    <div class="event-container">
                      <div
                        v-for="event in getActivitiesForDay(userPackage, dayIndex)"
                        :key="event.id"
                        class="event-card"
                        :style="{
                          backgroundColor: getActivityColor(event) || '#16A45A',
                          color: getContrastColor(getActivityColor(event) || '#16A45A'),
                        }"
                      >
                        <div class="event-content">
                          <span class="event-title">{{ event.name }}</span>
                          <inline-svg
                              v-if="
                                event.status === 1 
                              "
                              :src="'/media/svg/icons/Neolex/Basic/check.svg'"
                            />
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- Patient Detail Modal -->
    <patient-detail-calendar
      :visible="showDetailModal"
      :account-id="selectedAccountId"
      :coach-id="coachId"
      :activity-types="activityTypes"
      :selected-date="selectedDate"
      @close="closeDetailModal"
    />
  </div>
</template>

<script>
import PatientDetailCalendar from './PatientDetailCalendar.vue';

export default {
  components: {
    PatientDetailCalendar,
  },
  props: {
    userPackages: {
      type: Array,
      default: () => [],
    },
    activityTypes: {
      type: Array,
      default: () => [],
    },
    selectedServicePackage: {
      type: [String, Number],
      default: null,
    },
    servicePackages: {
      type: Array,
      default: () => [],
    },
    coachId: {
      type: String,
      default: '',
    },
    selectedDate: {
      type: [String, Date],
      default: null,
    },
  },
  data() {
    return {
      daysOfWeek: [
        'Thứ 2',
        'Thứ 3',
        'Thứ 4',
        'Thứ 5',
        'Thứ 6',
        'Thứ 7',
        'Chủ Nhật',
      ],
      defaultColor: '#16A45A',
      showDetailModal: false,
      selectedAccountId: '',
    };
  },
  computed: {
    baseDate() {
      if (this.selectedDate) {
        const d = new Date(this.selectedDate);
        if (!isNaN(d.getTime())) return d;
      }
      return new Date();
    },
    currentDayIndex() {
      const today = new Date();
      const todayDayIndex = today.getDay() === 0 ? 6 : today.getDay() - 1;
      if (!this.selectedDate) return todayDayIndex;
      // Nếu tuần được hiển thị là tuần hiện tại, highlight ngày hôm nay
      const mondayOfSelected = this.getMondayOfWeek(this.baseDate);
      const mondayOfToday = this.getMondayOfWeek(today);
      if (mondayOfSelected.getTime() === mondayOfToday.getTime()) {
        return todayDayIndex;
      }
      // Ngược lại highlight ngày đã chọn
      const selectedDay = this.baseDate.getDay();
      return selectedDay === 0 ? 6 : selectedDay - 1;
    },
    currentWeekDates() {
      const monday = this.getMondayOfWeek(this.baseDate);
      const dates = [];
      for (let i = 0; i < 7; i++) {
        const date = new Date(monday);
        date.setDate(monday.getDate() + i);
        dates.push(`${date.getDate()}/${date.getMonth() + 1}`);
      }
      return dates;
    },
  },
  methods: {
    getMondayOfWeek(date) {
      const d = new Date(date);
      const day = d.getDay();
      const diff = day === 0 ? -6 : 1 - day; // Thứ 2 là đầu tuần
      d.setDate(d.getDate() + diff);
      d.setHours(0, 0, 0, 0);
      return d;
    },
    getContrastColor(hexColor) {
      hexColor = hexColor.replace('#', '');
      const r = parseInt(hexColor.substr(0, 2), 16);
      const g = parseInt(hexColor.substr(2, 2), 16);
      const b = parseInt(hexColor.substr(4, 2), 16);

      const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

      return luminance < 0.5 ? '#FFFFFF' : '#000000';
    },

    getActivitiesForDay(userPackage, dayIndex) {
      if (!userPackage.activities || !Array.isArray(userPackage.activities)) {
        return [];
      }
      const monday = this.getMondayOfWeek(this.baseDate);
      const targetDate = new Date(monday);
      targetDate.setDate(monday.getDate() + dayIndex);
      const ty = targetDate.getFullYear();
      const tm = targetDate.getMonth() + 1;
      const td = targetDate.getDate();

      return userPackage.activities
        .filter((activity) => {
          if (!activity.tranStartDate) return false;
          const activityDate = new Date(activity.tranStartDate * 1000);
          return (
            activityDate.getFullYear() === ty &&
            activityDate.getMonth() + 1 === tm &&
            activityDate.getDate() === td
          );
        })
        .sort((a, b) => a.tranStartDate - b.tranStartDate);
    },
    async getAccountDetail () {
        this.$store.commit('context/setLoading', true);

      try {
        const params = new URLSearchParams();
        if(this.selectedCoach || this.selectedCoach.value) {
          params.append('coachId', this.selectedCoach.value);
        }
        if (this.selectedServicePackage) {
          params.append('servicePackageId', this.selectedServicePackage);
        }
        if (this.selectedActivityType) {
          params.append('activityTypeId', this.selectedActivityType);
        }
        if (this.selectedStatus !== null) {
          params.append('status', this.selectedStatus);
        }
        const url = `/TranUserPackage/Coach/Account-detail?${params.toString()}`;
        const data  = await this.$api.get(url);
          this.userPackages = data || [];
      } catch (error) {
        this.$toastr.e({
          title: 'Lỗi',
          msg: error,
        });
        return Promise.reject(error);
      } finally {
        this.$store.commit('context/setLoading', false);

      }
    },

    viewActivity(activity) {
      this.$emit('view-activity', activity);
    },
    getActivityColor(event) {
      const type = this.activityTypes.find(t => t.Id === event.activityTypeId);
      return type?.Color || null;
    },
    viewPatientDetail(accountId) {
      this.selectedAccountId = accountId;
      this.showDetailModal = true;
    },
    closeDetailModal() {
      this.showDetailModal = false;
      this.selectedAccountId = '';
    },
  },
};
</script>

<style scoped>
.app-container {
  padding: 0;
  width: 100%;
}
.font-weight-bold {
  font-weight: 700 !important;
}

.calendar-container {
  width: 100%;
  position: relative;
}

.calendar-wrapper {
  width: 100%;
  overflow-x: auto;
  scrollbar-width: thin;
  scroll-behavior: smooth;
}

.calendar-table {
  border-collapse: collapse;
  width: 100%;
  table-layout: fixed;
}

.name-column {
  width: 150px;
}

.day-column {
  width: calc((100% - 150px) / 7);
}

.header-row th {
  padding: 8px;
  border: 1px solid #ddd;
  background-color: #f5f5f5;
  font-weight: 500;
  text-align: center;
  font-size: 14px;
  white-space: nowrap;
}

.day-header {
  width: calc((100% - 150px) / 7);
  overflow: hidden;
  text-overflow: ellipsis;
}

.name-cell {
  width: 150px;
  text-align: left;
  padding: 8px;
  border: 1px solid #ddd;
  background-color: #f5f5f5;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.name-cell p {
  margin-bottom: 4px;
}

.name-cell .btn {
  font-size: 12px;
  padding: 4px 8px;
  white-space: nowrap;
}

.package-name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.day-cell {
  border: 1px solid #ddd;
  vertical-align: top;
  height: 80px;
  padding: 4px;
  width: calc((100% - 150px) / 7);
}

.event-container {
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.current-day {
  background-color: #e3f2fd !important;
}

.event-card {
  padding: 4px 8px;
  font-size: 12px;
  height: auto;
  display: flex;
  align-items: center;
  border-radius: 2px;
  margin-bottom: 5px;
}

.event-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.event-title {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  font-weight: 500;
}

.check-mark {
  color: #28a745;
  font-weight: bold;
  flex-shrink: 0;
}

.event-actions {
  display: flex;
  gap: 4px;
  margin-left: 8px;
  flex-shrink: 0;
}

.icon-button {
  padding: 0;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 12px;
  display: flex;
  align-items: center;
  opacity: 0.7;
}

.icon-button:hover {
  opacity: 1;
}



::v-deep .dropdown-form-filter .dropdown-menu {
    width: 700px;
  }

::v-deep .dropdown-menu dropdown-menu-right show {
    left: -99;
  }

.svg-icon {
  display: inline-block;
  margin-right: 0.5rem;
  width: 16px;
  height: 16px;
}

@media (max-width: 992px) {
  .name-column {
    width: 100px;
  }

  .name-cell {
    width: 100px;
  }

  .day-column {
    width: calc((100% - 100px) / 7);
  }

  .day-cell {
    width: calc((100% - 100px) / 7);
    height: 60px;
  }

  .event-card {
    padding: 2px 4px;
    font-size: 10px;
  }

  .event-actions {
    margin-left: 4px;
  }

  .icon-button {
    font-size: 10px;
  }

  .check-mark {
    font-size: 10px;
  }
}
</style>